import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import {
  FileText,
  Clock,
  Scale,
  User,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  mockActions,
  urgencyConfig,
  actionTypeConfig,
  type ActionItem,
} from './mockData';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Clock,
  Scale,
  User,
};

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 400, damping: 30 },
  },
};

function ActionRow({ action }: { action: ActionItem }) {
  const navigate = useNavigate();
  const urgencyStyles = urgencyConfig[action.urgency];
  const typeConfig = actionTypeConfig[action.type];
  const Icon = iconMap[typeConfig.icon] || FileText;

  return (
    <TableRow
      onClick={() => navigate({ to: action.link })}
      className="cursor-pointer hover:bg-slate-50 transition-colors"
    >
      {/* Type Icon */}
      <TableCell className="w-10 py-2">
        <div className={cn('p-1.5 rounded-md w-fit', urgencyStyles.bg)}>
          <Icon className="h-3.5 w-3.5 text-slate-600" />
        </div>
      </TableCell>

      {/* Task */}
      <TableCell className="py-2">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-slate-900">{action.title}</span>
          <span className="text-xs text-slate-500 truncate max-w-[300px]">
            {action.description}
          </span>
        </div>
      </TableCell>

      {/* Case Reference */}
      <TableCell className="py-2">
        <span className="text-xs font-mono text-slate-600">
          {action.description.match(/EU-\d{4}-\d{4}/)?.[0] || '-'}
        </span>
      </TableCell>

      {/* Due + Urgency */}
      <TableCell className="py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          <span className="text-xs text-slate-600">{action.dueIn}</span>
          <div className={cn('w-2 h-2 rounded-full', urgencyStyles.dot)} />
          {action.urgency === 'high' && (
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ActionItemsTable() {
  if (mockActions.length === 0) {
    return null;
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h2 className="font-semibold text-slate-900 text-sm">Handlungsbedarf</h2>
        </div>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
          {mockActions.length} Einträge
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10 py-2 text-xs">Typ</TableHead>
            <TableHead className="py-2 text-xs">Aufgabe</TableHead>
            <TableHead className="py-2 text-xs">Fall</TableHead>
            <TableHead className="py-2 text-xs text-right">Fällig</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {mockActions.map((action) => (
            <ActionRow key={action.id} action={action} />
          ))}
        </TableBody>
      </Table>
    </motion.div>
  );
}
